import { defineStore } from "pinia";
import { ref, watch } from "vue";

import type { BoardType } from "@/model/baseTypes";
import type { BoardData } from "@/model/board";
import type { FlexType } from "@/model/flexboard";
import type { Art, Session, Team } from "@/model/session";
import { LocalStore, versioned } from "@/utils/LocalStore";

import { useSessionStore } from "./session";

type State = { sessionId?: Session["id"] } & Record<Session["id"], SessionNavigation>;

interface SessionNavigation {
  boardType?: BoardType;
  artId?: string;
  teamInArt: Record<Art["id"], Team["id"]>;
  flexInType: Record<FlexType["id"], BoardData<"flex">["id"]>;
}

export const useNavigationStore = defineStore("navigation", () => {
  const store = new LocalStore<State>("navigation", { sedes: versioned(1) });
  const state = ref(load());

  watch(state, () => store.save(state.value), { deep: true });

  function load() {
    const state = store.load() ?? {};
    // stored values may not have flexInType
    for (const session in state) {
      if (session !== "sessionId") {
        state[session].flexInType = state[session].flexInType ?? {};
      }
    }
    return state;
  }

  function lastSelectedSessionId() {
    return state.value.sessionId;
  }

  function lastSelectedBoard(sessionId?: Session["id"]) {
    return sessionState(sessionId).boardType;
  }

  function lastSelectedArtId(sessionId?: Session["id"]) {
    return sessionState(sessionId).artId;
  }

  function lastSelectedTeamInArtId(artId: string) {
    return sessionState().teamInArt[artId];
  }

  function lastSelectedTeamId(sessionId?: Session["id"]) {
    const artId = lastSelectedArtId(sessionId);
    if (artId) {
      return sessionState(sessionId).teamInArt[artId];
    }
  }

  function lastSelectedFlexId(flexTypeId: string) {
    return sessionState().flexInType[flexTypeId];
  }

  function setSessionId(sessionId: Session["id"]) {
    state.value.sessionId = sessionId;
  }

  function setBoardType(board: BoardType) {
    sessionState().boardType = board;
  }

  function setArtId(artId: string) {
    sessionState().artId = artId;
  }

  function setTeamInArt(team: Team) {
    sessionState().teamInArt[team.artId || ""] = team.id;
  }

  function setFlexInType(flex: BoardData<"flex">) {
    sessionState().flexInType[flex.flexType.id] = flex.id;
  }

  function sessionState(id?: Session["id"]) {
    const sessionId = id || useSessionStore().current.id;
    if (!(sessionId in state.value)) {
      state.value[sessionId] = { teamInArt: {}, flexInType: {} };
    }
    return state.value[sessionId];
  }

  return {
    lastSelectedSessionId,
    lastSelectedBoard,
    lastSelectedArtId,
    lastSelectedTeamInArtId,
    lastSelectedTeamId,
    lastSelectedFlexId,
    setSessionId,
    setBoardType,
    setArtId,
    setTeamInArt,
    setFlexInType,
  };
});
